import { Heart, TrendingUp, Calendar, Tag, AlertCircle } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { DonationService } from "../services/APIs";
import { Skeleton } from "../components/ui/skeleton";
import BackButton from "../components/BackButton";
import CitycollabNavigationRoutes from "../utils/navigationRoutes";

const Donations = () => {
  const navigate = useNavigate();

  const { data: userDonations, isLoading, isError } = useQuery({
    queryKey: ["userDonations"],
    queryFn: () => DonationService.getLoginUserDonations(),
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background pb-20 md:pb-8">
        <header className="sticky top-0 z-50 border-b border-border bg-card shadow-sm">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center gap-3">
              <BackButton fallback={CitycollabNavigationRoutes.NEW_DESIGN} />
              <div>
                <h1 className="text-lg font-bold text-primary md:text-xl">
                  Donations
                </h1>
                <p className="text-xs text-muted-foreground">
                  Support organization causes
                </p>
              </div>
            </div>
          </div>
        </header>

        <main className="container mx-auto px-4 py-6 md:py-8">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="overflow-hidden">
                <CardHeader>
                  <Skeleton className="h-6 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-full" />
                </CardHeader>
                <CardContent className="space-y-4">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-2 w-full" />
                  <div className="flex justify-between">
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-4 w-20" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </main>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
        <AlertCircle className="h-12 w-12 text-destructive mb-4" />
        <h2 className="text-xl font-bold mb-2">Failed to load donations</h2>
        <p className="text-muted-foreground mb-4">There was an error fetching your donation history.</p>
        <BackButton fallback={CitycollabNavigationRoutes.NEW_DESIGN} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-8">
      <header className="sticky top-0 z-50 border-b border-border bg-card shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <BackButton fallback={CitycollabNavigationRoutes.NEW_DESIGN} />
            <div>
              <h1 className="text-lg font-bold text-primary md:text-xl">
                Donations
              </h1>
              <p className="text-xs text-muted-foreground">
                Your donation history and contributions
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 md:py-8">
        {!userDonations || userDonations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Heart className="h-16 w-16 text-muted-foreground/30 mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">No donations yet</h2>
            <p className="text-muted-foreground max-w-xs">
              You haven't made any donations yet. Support our causes to see them here!
            </p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {userDonations.map((donation, index) => {
              const amount = parseFloat(donation.donation_amount);
              // For individual donations, we might not have a goal, so we show it as 100% completed
              const progress = 100;

              return (
                <Card
                  key={donation.donation_id}
                  className="cursor-pointer overflow-hidden transition-all hover:-translate-y-1 hover:shadow-lg"
                  onClick={() => navigate(`/donations/${donation.donation_id}`)}
                >
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-lg">{donation.donation_title}</CardTitle>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                        donation.status === "Paid" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
                      }`}>
                        {donation.status}
                      </span>
                    </div>
                    <CardDescription className="flex items-center gap-1">
                      <Tag className="h-3 w-3" />
                      {donation.category}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <div className="mb-2 flex justify-between text-sm">
                        <span className="font-bold text-primary">
                          ${amount.toLocaleString()}
                        </span>
                        <span className="text-muted-foreground text-xs flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {donation.donation_date}
                        </span>
                      </div>
                      <Progress value={progress} className="h-2" />
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2 text-muted-foreground text-xs">
                        <Heart className="h-4 w-4" />
                        <span>Support contributing</span>
                      </div>
                      <div className="flex items-center gap-1 font-medium text-primary">
                        <TrendingUp className="h-4 w-4" />
                        <span>{donation.status}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default Donations;
